import { format, isValid } from "date-fns";

/** Day-first display used across the manager app */
export const MANAGE_DATE_DISPLAY = "dd/MM/yy";

function normalizeDateString(s) {
  // MySQL-style "yyyy-MM-dd HH:mm:ss" → ISO so time is preserved
  const spaceMatch = s.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/);
  if (spaceMatch) {
    const hasTz = /[Zz]|[+-]\d{2}:?\d{2}$/.test(s);
    return `${spaceMatch[1]}T${spaceMatch[2]}${hasTz ? "" : "Z"}`;
  }

  const idxT = s.indexOf("T");
  if (idxT > 0 && /^\d{4}-\d{2}-\d{2}$/.test(s.slice(0, idxT))) {
    const timePart = s.slice(idxT + 1);
    // Only strip time for date-only values (delivery_date), not real timestamps
    const isMidnightOnly =
      !timePart ||
      /^00:00:00(\.0+)?(Z)?$/i.test(timePart);
    if (isMidnightOnly) {
      return s.slice(0, idxT);
    }
  }
  return s;
}

/**
 * Parse API / UI values to a local Date, or null.
 * yyyy-MM-dd (and ISO strings starting with that date) use calendar components (no UTC shift).
 */
export function parseManageDateInput(input) {
  if (input == null || input === "") return null;
  if (input instanceof Date) return isValid(input) ? input : null;
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  const normalized = normalizeDateString(trimmed);
  const ymdOnly = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdOnly) {
    const y = Number(ymdOnly[1]);
    const m = Number(ymdOnly[2]) - 1;
    const d = Number(ymdOnly[3]);
    const local = new Date(y, m, d);
    return isValid(local) ? local : null;
  }

  const instant = new Date(trimmed);
  return isValid(instant) ? instant : null;
}

/**
 * Format a date or ISO / yyyy-MM-dd string for labels (dd/MM/yy).
 * Unknown strings are returned unchanged.
 */
export function formatManageDate(input) {
  if (input == null || input === "") return "";
  if (typeof input === "string" && /^\d{1,2}-\d{1,2}$/.test(input.trim())) {
    const [a, b] = input.trim().split("-");
    return `${String(a).padStart(2, "0")}/${String(b).padStart(2, "0")}`;
  }
  const d = parseManageDateInput(input);
  if (!d) return typeof input === "string" ? input : "";
  return format(d, MANAGE_DATE_DISPLAY);
}

/** Date + time for timestamps (e.g. order created_at) */
export function formatManageDateTime(input) {
  if (input == null || input === "") return "";
  const d = parseManageDateTimeUtc(input);
  if (!d) return "";
  return `${format(d, MANAGE_DATE_DISPLAY)}, ${format(d, "h:mm a")}`;
}

/** Parse timestamp as UTC instant (for server-stored completion / feedback times). */
export function parseManageDateTimeUtc(input) {
  if (input == null || input === "") return null;
  if (input instanceof Date) return isValid(input) ? input : null;
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  let normalized = normalizeDateString(trimmed);
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    normalized = `${normalized}T00:00:00Z`;
  } else if (!/[Zz]|[+-]\d{2}:?\d{2}$/.test(normalized)) {
    normalized = normalized.includes("T") ? `${normalized}Z` : `${normalized}T00:00:00Z`;
  }
  const d = new Date(normalized);
  return isValid(d) ? d : null;
}

const IST_TIMEZONE = "Asia/Kolkata";

/** Completion / feedback times — always India time (IST), avoids 8:18-style display bugs. */
export function formatManageDateTimeIST(input) {
  const d = parseManageDateTimeUtc(input);
  if (!d) return "";
  const datePart = d.toLocaleDateString("en-GB", {
    timeZone: IST_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
  const timePart = d.toLocaleTimeString("en-IN", {
    timeZone: IST_TIMEZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
}

/** Short label: "22 May" (IST). */
export function formatManageDayMonthIST(input) {
  const d = parseManageDateTimeUtc(input);
  if (!d) return "";
  return d.toLocaleDateString("en-IN", {
    timeZone: IST_TIMEZONE,
    day: "numeric",
    month: "short",
  });
}

/** Time only: "12:18 pm" (IST). */
export function formatManageTimeIST(input) {
  const d = parseManageDateTimeUtc(input);
  if (!d) return "";
  return d.toLocaleTimeString("en-IN", {
    timeZone: IST_TIMEZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** yyyy-MM (calendar month) → MM/yyyy */
export function formatManageYearMonth(ym) {
  if (!ym || typeof ym !== "string") return ym || "";
  const m = ym.match(/^(\d{4})-(\d{2})$/);
  if (!m) return ym;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = new Date(y, mo, 1);
  if (!isValid(d)) return ym;
  return format(d, "MM/yyyy");
}
